/**
 * Printer Settings Component
 * Simplified UI:
 * - Connection status display
 * - Print options (toggles)
 * - Footer text (only editable receipt field)
 * - Preview button
 * - No browser print option (removed)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { 
  Printer, Usb, TestTube, CheckCircle, XCircle, 
  Loader2, RefreshCw, Eye, Settings2, Plug, PlugZap
} from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import nativeThermalPrinter from '../services/nativeThermalPrinterService';

const PrinterSettings = ({ open, onOpenChange, franchiseData }) => {
  const [settings, setSettings] = useState(nativeThermalPrinter.getSettings());
  const [testing, setTesting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState({
    connected: false,
    deviceName: null,
    printerAvailable: false
  });
  
  const isNative = Capacitor.isNativePlatform();

  // Subscribe to printer status changes
  useEffect(() => {
    if (!isNative) return;
    
    const unsubscribe = nativeThermalPrinter.addStatusListener((status) => {
      setConnectionStatus(status);
    });
    
    return () => unsubscribe();
  }, [isNative]);

  // Refresh status when dialog opens
  useEffect(() => {
    if (open && isNative) {
      nativeThermalPrinter.refreshStatus();
    }
  }, [open, isNative]);

  const handleSettingChange = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    nativeThermalPrinter.updateSettings(newSettings);
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      // First try to get permission if needed
      const permResult = await nativeThermalPrinter.requestPermission();
      if (!permResult.success) {
        toast.error('USB permission denied');
        return;
      }
      
      // Then connect
      const result = await nativeThermalPrinter.connect();
      if (result.success) {
        toast.success(`Connected to ${result.deviceName || 'USB Printer'}`);
      } else {
        toast.error(result.error || 'Failed to connect');
      }
    } catch (error) {
      toast.error('Connection failed: ' + error.message);
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await nativeThermalPrinter.disconnect();
      toast.info('Printer disconnected');
    } catch (error) {
      toast.error('Disconnect failed');
    }
  };

  const handleRefreshStatus = async () => {
    await nativeThermalPrinter.refreshStatus();
  };

  const handleTestPrint = async () => {
    setTesting(true);
    try {
      const result = await nativeThermalPrinter.testPrint(franchiseData || {
        brand_name: '1946 Filter Coffee & Tea',
        franchise_name: 'Sample Outlet',
        contact_number: '9876543210',
        franchise_address: '123 Main Street',
        pincode: '560001',
        gstin: ''
      });
      
      if (result.success) {
        toast.success('Test receipt printed successfully!');
      } else {
        toast.error(result.error || 'Test print failed');
      }
    } catch (error) {
      toast.error('Test print failed: ' + error.message);
    } finally {
      setTesting(false);
    }
  };

  // Connection status component
  const ConnectionStatusCard = () => {
    if (!isNative) {
      return (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
          <div className="flex items-center gap-3">
            <Printer className="h-6 w-6 text-yellow-600" />
            <div>
              <p className="font-semibold text-yellow-800">Web Browser Mode</p>
              <p className="text-sm text-yellow-600">
                USB printing is only available in the Android app. 
                Please use the FOS Franchise app for thermal printing.
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (connectionStatus.connected) {
      return (
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-full">
                <PlugZap className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="font-semibold text-green-800 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Connected
                </p>
                <p className="text-sm text-green-600">
                  {connectionStatus.deviceName || 'USB Thermal Printer'}
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleDisconnect}>
              Disconnect
            </Button>
          </div>
        </div>
      );
    }

    if (connectionStatus.printerAvailable) {
      return (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-full">
                <Usb className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-blue-800">Printer Detected</p>
                <p className="text-sm text-blue-600">
                  {connectionStatus.deviceName || 'USB Printer'} - Ready to connect
                </p>
              </div>
            </div>
            <Button 
              onClick={handleConnect} 
              disabled={connecting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {connecting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Connecting...</>
              ) : (
                <><Plug className="h-4 w-4 mr-2" /> Connect</>
              )}
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-full">
              <XCircle className="h-6 w-6 text-gray-400" />
            </div>
            <div>
              <p className="font-semibold text-gray-700">No Printer Found</p>
              <p className="text-sm text-gray-500">
                Connect a USB thermal printer via OTG cable
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleRefreshStatus}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>
    );
  };

  // Receipt Preview component
  const ReceiptPreview = () => {
    const width = settings.paperWidth === '58mm' ? 32 : 48;
    const brandName = franchiseData?.brand_name || franchiseData?.name || '1946 Filter Coffee & Tea';
    const outletName = franchiseData?.franchise_name || franchiseData?.name || 'Sample Outlet';
    const contact = franchiseData?.contact_number || '9876543210';
    const address = franchiseData?.franchise_address || '123 Main Street';
    const pincode = franchiseData?.pincode || '560001';
    const gstin = franchiseData?.gstin || '';

    return (
      <div className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-4 font-mono text-xs leading-relaxed max-h-96 overflow-y-auto">
        {/* Brand Name */}
        <div className="text-center font-bold text-sm mb-1">
          {brandName}
        </div>
        
        <div className="border-t border-dashed border-gray-400 my-2"></div>
        
        {/* Order Info */}
        <div className="text-left text-gray-700">
          <div>Order #: TEST-001</div>
          {outletName !== brandName && <div>Outlet: {outletName}</div>}
          <div>Tel: {contact}</div>
          <div>{address} - {pincode}</div>
          {gstin && <div>GSTIN: {gstin}</div>}
          <div>Date: {new Date().toLocaleString('en-IN')}</div>
          
          {settings.showOrderDetails && (
            <div>Type: DINE IN | Table: 5</div>
          )}
          
          {settings.showCustomerInfo && (
            <>
              <div>Customer: Test Customer</div>
              <div>Mobile: 9876543210</div>
            </>
          )}
        </div>
        
        <div className="border-t-2 border-gray-400 my-2"></div>
        
        {/* Items Header */}
        <div className="flex justify-between font-semibold text-gray-800">
          <span>Item</span>
          <span className="flex gap-4">
            <span>Qty</span>
            <span>Amt</span>
          </span>
        </div>
        
        <div className="border-t border-dashed border-gray-300 my-1"></div>
        
        {/* Items */}
        <div className="space-y-1 text-gray-700">
          <div className="flex justify-between">
            <span>Filter Coffee</span>
            <span className="flex gap-4">
              <span className="w-6 text-right">2</span>
              <span className="w-12 text-right">36.00</span>
            </span>
          </div>
          <div className="flex justify-between">
            <span>Masala Tea</span>
            <span className="flex gap-4">
              <span className="w-6 text-right">1</span>
              <span className="w-12 text-right">20.00</span>
            </span>
          </div>
          <div className="flex justify-between">
            <span>Butter Biscuit</span>
            <span className="flex gap-4">
              <span className="w-6 text-right">3</span>
              <span className="w-12 text-right">15.00</span>
            </span>
          </div>
        </div>
        
        <div className="border-t border-gray-400 my-2"></div>
        
        {/* Totals */}
        <div className="space-y-1">
          <div className="flex justify-between text-gray-700">
            <span>SUBTOTAL:</span>
            <span>71.00</span>
          </div>
          
          {settings.showTaxBreakdown && (
            <>
              <div className="flex justify-between text-gray-600 text-xs">
                <span>CGST (2.5%):</span>
                <span>1.78</span>
              </div>
              <div className="flex justify-between text-gray-600 text-xs">
                <span>SGST (2.5%):</span>
                <span>1.78</span>
              </div>
            </>
          )}
          
          <div className="border-t border-gray-400 my-1"></div>
          
          <div className="flex justify-between font-bold text-gray-900">
            <span>TOTAL:</span>
            <span>Rs. 71.00</span>
          </div>
          
          <div className="flex justify-between text-gray-600">
            <span>Payment:</span>
            <span>CASH</span>
          </div>
        </div>
        
        <div className="border-t-2 border-gray-400 my-2"></div>
        
        {/* Footer */}
        <div className="text-center text-gray-600">
          <div>{settings.footerText || 'Thank you for your visit!'}</div>
          <div>Visit Again!</div>
        </div>
        
        <div className="border-t-2 border-gray-400 mt-2"></div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-primary flex items-center gap-2">
            <Printer className="h-5 w-5" />
            Printer Settings
          </DialogTitle>
          <DialogDescription>
            Configure your thermal receipt printer
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Connection Status */}
          <div className="space-y-2">
            <Label className="text-base font-semibold flex items-center gap-2">
              <Usb className="h-4 w-4" /> Connection Status
            </Label>
            <ConnectionStatusCard />
          </div>

          {/* Print Options */}
          <div className="space-y-4 p-4 bg-slate-50 border border-slate-200 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Settings2 className="h-5 w-5 text-slate-600" />
              <Label className="text-base font-semibold">Print Options</Label>
            </div>

            {/* Paper Width */}
            <div className="space-y-2">
              <Label>Paper Width</Label>
              <Select
                value={settings.paperWidth}
                onValueChange={(value) => handleSettingChange('paperWidth', value)}
              >
                <SelectTrigger data-testid="paper-width-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="58mm">58mm (2 inch)</SelectItem>
                  <SelectItem value="80mm">80mm (3 inch)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Toggle Options */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Auto Cut Paper</Label>
                  <p className="text-xs text-gray-500">Cut receipt after printing</p>
                </div>
                <Switch
                  checked={settings.autoCut}
                  onCheckedChange={(checked) => handleSettingChange('autoCut', checked)}
                  data-testid="auto-cut-switch"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Open Cash Drawer</Label>
                  <p className="text-xs text-gray-500">Open drawer after print</p>
                </div>
                <Switch
                  checked={settings.openDrawer}
                  onCheckedChange={(checked) => handleSettingChange('openDrawer', checked)}
                  data-testid="open-drawer-switch"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Show Order Details</Label>
                  <p className="text-xs text-gray-500">Order type, table/token</p>
                </div>
                <Switch
                  checked={settings.showOrderDetails !== false}
                  onCheckedChange={(checked) => handleSettingChange('showOrderDetails', checked)}
                  data-testid="show-order-details-switch"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Show Tax Breakdown</Label>
                  <p className="text-xs text-gray-500">CGST/SGST details</p>
                </div>
                <Switch
                  checked={settings.showTaxBreakdown === true}
                  onCheckedChange={(checked) => handleSettingChange('showTaxBreakdown', checked)}
                  data-testid="show-tax-switch"
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Show Customer Info</Label>
                  <p className="text-xs text-gray-500">Name & mobile (if provided)</p>
                </div>
                <Switch
                  checked={settings.showCustomerInfo !== false}
                  onCheckedChange={(checked) => handleSettingChange('showCustomerInfo', checked)}
                  data-testid="show-customer-switch"
                />
              </div>
            </div>

            {/* Print Copies */}
            <div className="space-y-2">
              <Label>Number of Copies</Label>
              <Select
                value={settings.printCopies?.toString() || '1'}
                onValueChange={(value) => handleSettingChange('printCopies', parseInt(value))}
              >
                <SelectTrigger data-testid="print-copies-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 Copy</SelectItem>
                  <SelectItem value="2">2 Copies</SelectItem>
                  <SelectItem value="3">3 Copies</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Receipt Footer Text (only editable receipt field) */}
          <div className="space-y-2">
            <Label htmlFor="footerText" className="text-base font-semibold">
              Receipt Footer Text
            </Label>
            <Input
              id="footerText"
              type="text"
              placeholder="Thank you for your visit!"
              value={settings.footerText || ''}
              onChange={(e) => handleSettingChange('footerText', e.target.value)}
              data-testid="footer-text-input"
            />
            <p className="text-xs text-gray-500">
              This message will appear at the bottom of every receipt
            </p>
          </div>

          {/* Preview Toggle */}
          <div className="space-y-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowPreview(!showPreview)}
              className="w-full"
              data-testid="preview-receipt-btn"
            >
              <Eye className="h-4 w-4 mr-2" />
              {showPreview ? 'Hide Receipt Preview' : 'Show Receipt Preview'}
            </Button>

            {showPreview && <ReceiptPreview />}
          </div>

          {/* Test Print Button */}
          <Button
            onClick={handleTestPrint}
            disabled={testing || !isNative || !connectionStatus.connected}
            className="w-full"
            data-testid="test-print-btn"
          >
            {testing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Printing Test Receipt...
              </>
            ) : (
              <>
                <TestTube className="h-4 w-4 mr-2" />
                Print Test Receipt
              </>
            )}
          </Button>
          
          {!isNative && (
            <p className="text-xs text-center text-amber-600">
              Test print is only available in the Android app
            </p>
          )}
          
          {isNative && !connectionStatus.connected && (
            <p className="text-xs text-center text-gray-500">
              Connect a printer to enable test print
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PrinterSettings;
